import { AUDIT_DETAIL_PARAMS, type AuditDetailKey } from '../presentation-catalog.js';
// SCR-13 access & change log (plan 8.4.13, 11.8). Append-only hash chain with daily external anchors.
// Viewing this screen and running the integrity check are themselves audited. Details travel as i18n key + params.
import { z } from 'zod';
import { AuditKind, AuditResult, CaseStatus, ReasonCategory, RetentionClass } from '../enums.js';
import { Comparison, Count, EpochMs, Id, LocalizedText, PageMeta, PersonRef, RecordRef, RoleRef, UserId, Ymd, csvOf, periodQuery } from '../common.js';

/** The single action-code table (plan 11.8.3): action → filter kind + retention grade. Unknown codes are 'other'/1y. */
export const AUDIT_ACTIONS = {
  login: { kind: 'login', retention: 'access_2y' },
  'login.failed': { kind: 'login', retention: 'access_2y' },
  'login.locked': { kind: 'login', retention: 'access_2y' },
  'login.revoke': { kind: 'login', retention: 'access_2y' },
  logout: { kind: 'login', retention: 'access_2y' },
  reauth: { kind: 'login', retention: 'access_2y' },
  'password.change': { kind: 'login', retention: 'access_2y' },
  'mfa.enroll': { kind: 'login', retention: 'permission_3y' },
  'pledge.ack': { kind: 'permission', retention: 'permission_3y' },
  'role.create': { kind: 'permission', retention: 'permission_3y' },
  'role.change': { kind: 'permission', retention: 'permission_3y' },
  'role.delete': { kind: 'permission', retention: 'permission_3y' },
  'role.grant': { kind: 'permission', retention: 'permission_3y' },
  'role.expiry_deferred': { kind: 'permission', retention: 'permission_3y' },
  'role.revoke': { kind: 'permission', retention: 'permission_3y' },
  'approval.request': { kind: 'permission', retention: 'permission_3y' },
  'approval.approve': { kind: 'permission', retention: 'permission_3y' },
  'approval.reject': { kind: 'permission', retention: 'permission_3y' },
  'approval.withdraw': { kind: 'permission', retention: 'permission_3y' },
  'access.review': { kind: 'permission', retention: 'permission_3y' },
  'case.create': { kind: 'permission', retention: 'access_2y' },
  'case.approve': { kind: 'permission', retention: 'access_2y' },
  'case.close': { kind: 'permission', retention: 'access_2y' },
  'case.defer_notice': { kind: 'permission', retention: 'access_2y' },
  'person.create': { kind: 'permission', retention: 'permission_3y' },
  'person.disable': { kind: 'permission', retention: 'permission_3y' },
  'person.enable': { kind: 'permission', retention: 'permission_3y' },
  'person.reset_password': { kind: 'permission', retention: 'permission_3y' },
  'team.create': { kind: 'permission', retention: 'permission_3y' },
  'team.update': { kind: 'permission', retention: 'permission_3y' },
  'team.archive': { kind: 'permission', retention: 'permission_3y' },
  'team.delete': { kind: 'permission', retention: 'permission_3y' },
  'team.move': { kind: 'permission', retention: 'permission_3y' },
  'team.managers': { kind: 'permission', retention: 'permission_3y' },
  'invite.create': { kind: 'permission', retention: 'permission_3y' },
  'invite.resend': { kind: 'permission', retention: 'permission_3y' },
  'invite.cancel': { kind: 'permission', retention: 'permission_3y' },
  'invite.accept': { kind: 'permission', retention: 'permission_3y' },
  'sa.update': { kind: 'permission', retention: 'permission_3y' },
  'sa.create': { kind: 'permission', retention: 'permission_3y' },
  'sa.suspend': { kind: 'permission', retention: 'permission_3y' },
  'sa.resume': { kind: 'permission', retention: 'permission_3y' },
  'sa.token': { kind: 'key_rotation', retention: 'general_1y' },
  'policy.change': { kind: 'policy', retention: 'general_1y' },
  'notice.publish': { kind: 'policy', retention: 'general_1y' },
  'price.version': { kind: 'policy', retention: 'general_1y' },
  'content.view': { kind: 'content_view', retention: 'access_2y' },
  'content.grant': { kind: 'content_view', retention: 'access_2y' },
  'person.view': { kind: 'person_view', retention: 'access_2y' },
  export: { kind: 'export', retention: 'access_2y' },
  'consent.change': { kind: 'consent', retention: 'access_2y' },
  'connection.add': { kind: 'consent', retention: 'general_1y' },
  'deletion.request': { kind: 'deletion', retention: 'access_2y' },
  'deletion.cancel': { kind: 'deletion', retention: 'access_2y' },
  'deletion.process': { kind: 'deletion', retention: 'access_2y' },
  'retention.run': { kind: 'deletion', retention: 'general_1y' },
  'legal_hold.set': { kind: 'legal_hold', retention: 'access_2y' },
  'legal_hold.release': { kind: 'legal_hold', retention: 'access_2y' },
  'legal_hold.defer_notice': { kind: 'legal_hold', retention: 'access_2y' },
  'apikey.create': { kind: 'key_rotation', retention: 'general_1y' },
  'apikey.update': { kind: 'key_rotation', retention: 'general_1y' },
  'apikey.rotate': { kind: 'key_rotation', retention: 'general_1y' },
  'credential.create': { kind: 'key_rotation', retention: 'general_1y' },
  'credential.rotate': { kind: 'key_rotation', retention: 'general_1y' },
  'credential.revoke': { kind: 'key_rotation', retention: 'general_1y' },
  'gateway.models.update': { kind: 'policy', retention: 'general_1y' },
  'key.rotate': { kind: 'key_rotation', retention: 'general_1y' },
  'token.issue': { kind: 'key_rotation', retention: 'general_1y' },
  'token.revoke': { kind: 'key_rotation', retention: 'general_1y' },
  'glossary.add': { kind: 'glossary', retention: 'general_1y' },
  'glossary.edit': { kind: 'glossary', retention: 'general_1y' },
  'glossary.revert': { kind: 'glossary', retention: 'general_1y' },
  'glossary.hide': { kind: 'glossary', retention: 'general_1y' },
  'glossary.unhide': { kind: 'glossary', retention: 'general_1y' },
  'glossary.linkcheck': { kind: 'glossary', retention: 'general_1y' },
  'audit.view': { kind: 'other', retention: 'access_2y' },
  'audit.verify': { kind: 'other', retention: 'access_2y' },
  'audit.review': { kind: 'other', retention: 'access_2y' },
  'org.sync': { kind: 'other', retention: 'general_1y' },
  /** An applied change taken back inside its undo window; the detail names the undone action. */
  'change.undo': { kind: 'permission', retention: 'permission_3y' },
} as const satisfies Record<string, { kind: AuditKind; retention: RetentionClass }>;
export type AuditAction = keyof typeof AUDIT_ACTIONS;
export const AUDIT_ACTION_CODES = Object.keys(AUDIT_ACTIONS) as [AuditAction, ...AuditAction[]];
export const AuditActionCode = z.enum(AUDIT_ACTION_CODES);

/** Filter chips: content views split into success/denied (plan 8.4.13). */
export const AUDIT_CHIPS = ['content_ok', 'content_denied', 'person_view', 'permission', 'policy', 'login', 'export', 'consent', 'deletion', 'legal_hold', 'key_rotation', 'glossary', 'other'] as const;
export const AuditChip = z.enum(AUDIT_CHIPS);
export type AuditChip = z.infer<typeof AuditChip>;

/** Structured detail: web i18n `audit.detail.<key>` with params (no free sentences from the server). */
/**
 * Detail = up to 6 parts the web joins with " · " (mockup admin.js dx lists). A param may be a list (team names) or
 * text frozen in both languages at write time (role names survive the role's deletion).
 */
export const AuditDetailParam = z.union([z.string(), z.number(), z.boolean(), z.null(), z.array(z.string()).max(50), LocalizedText]);
export const AuditDetailPart = z.object({ key: z.enum(Object.keys(AUDIT_DETAIL_PARAMS) as [AuditDetailKey, ...AuditDetailKey[]]), params: z.record(z.string(), AuditDetailParam) });
export const AuditDetail = z.array(AuditDetailPart).min(1).max(6);
export type AuditDetail = z.infer<typeof AuditDetail>;

export const AuditActor = z.discriminatedUnion('kind', [z.object({ kind: z.literal('person'), person: PersonRef }), z.object({ kind: z.literal('system') })]);

export const AuditEntry = z.object({
  seq: z.int().positive(),
  at: EpochMs,
  action: AuditActionCode,
  kind: AuditKind,
  chip: AuditChip,
  result: AuditResult,
  actor: AuditActor,
  actorRole: RoleRef.nullable(),
  /** `caseCode` = the investigation case's display code ("INV-2026-004"); ids stay in `tech`. */
  target: z.object({ type: z.string(), person: PersonRef.nullable(), record: RecordRef.nullable(), caseId: Id.nullable(), caseCode: z.string().nullable() }),
  reasonCategory: ReasonCategory.nullable(),
  reason: z.string().nullable(),
  detail: AuditDetail.nullable(),
  retentionClass: RetentionClass,
  /** Admin-privileged actors: original IP; ordinary users' login rows: partially masked. */
  ip: z.string().nullable(),
  /** "어디서" also names the device (plan 11.8.3): browser · OS summary for web actions, the PC name for collector events. */
  device: z.string().max(80).nullable(),
  /** "기술 상세 보기" fold: target type, internal ids, original raw detail. */
  tech: z.object({ targetType: z.string(), ids: z.array(z.string()), raw: z.string().nullable() }),
  prevHash: z.string(),
  hash: z.string(),
});
export type AuditEntry = z.infer<typeof AuditEntry>;

export const SeqParams = z.object({ seq: z.coerce.number().int().positive() });

/** GET /api/v1/admin/audit — 20 rows a page; the first page view writes `audit.view`. */
export const AuditListQuery = periodQuery({
  kinds: csvOf(AuditChip).optional(),
  person: UserId.optional(),
  q: z.string().trim().max(60).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
});
export const AuditListResponse = z.object({
  items: z.array(AuditEntry),
  page: PageMeta,
  chipCounts: z.partialRecord(AuditChip, Count),
  deniedOrFailed: Count,
  /** "'로그인' 종류를 빼면 n건 있어요". */
  suggestions: z.array(z.object({ dropChip: AuditChip, count: Count })),
});
export const AuditEntryResponse = z.object({
  entry: AuditEntry,
  /** First anchor covering this row, or null = joins the anchor after midnight. */
  anchor: z.object({ date: Ymd, seq: z.int().positive(), signedAt: EpochMs }).nullable(),
});

/** Daily external anchor (plan 11.8.1): date, last covered seq, when it was signed and where it is kept. */
export const AnchorInfo = z.object({ date: Ymd, seq: z.int().positive(), signedAt: EpochMs, store: z.enum(['file', 'object_storage', 'email', 'other']) });
/** One row of the chain preview (seq · hash prefix · action). */
export const ChainSample = z.object({ seq: z.int().positive(), hashPrefix: z.string().max(12), action: AuditActionCode });

export const BulkViewAlert = z.object({ actorRole: RoleRef.nullable(), actor: PersonRef.nullable(), count: Count, from: EpochMs, to: EpochMs, caseId: Id.nullable(), caseCode: z.string().nullable() });
export const AuditKpisQuery = periodQuery({});
export const AuditKpisResponse = z.object({
  entries: Comparison,
  contentViews: z.object({ ok: Count, denied: Count }),
  permissionChanges: Count,
  policyChanges: Count,
  retentionClasses: z.record(RetentionClass, Count),
  totalAllTime: Count,
  /** Last check (automatic daily or manual) with the chain preview and the anchor it was compared with. */
  lastVerify: z
    .object({
      status: z.enum(['ok', 'chain_broken', 'anchor_mismatch']),
      at: EpochMs,
      auto: z.boolean(),
      count: Count,
      brokenAtSeq: z.int().positive().nullable(),
      anchor: AnchorInfo.nullable(),
      sample: z.array(ChainSample).max(12),
    })
    .nullable(),
  bulkAlert: BulkViewAlert.nullable(),
});

/** POST /api/v1/admin/audit/verify (audit.verify) — chain recompute + external anchor comparison; logged. */
export const AuditVerifyResponse = z.object({
  status: z.enum(['ok', 'chain_broken', 'anchor_mismatch']),
  count: Count,
  brokenAtSeq: z.int().positive().nullable(),
  anchor: AnchorInfo.extend({ ok: z.boolean() }).nullable(),
  sample: z.array(ChainSample).max(12),
  checkedAt: EpochMs,
});

/** Monthly access-record review (plan 11.8.4), month-to-date in the display time zone. */
export const AuditReportQuery = z.object({ month: z.string().regex(/^\d{4}-\d{2}$/) });
export const AuditReportResponse = z.object({
  month: z.string(),
  byViewer: z.array(z.object({ actor: PersonRef.nullable(), role: RoleRef.nullable(), views: Count, denied: Count })),
  byTarget: z.array(z.object({ person: PersonRef, views: Count })),
  offHours: z.array(z.object({ at: EpochMs, actor: PersonRef.nullable(), role: RoleRef.nullable(), record: RecordRef.nullable(), target: PersonRef.nullable() })),
  exports: z.array(z.object({ at: EpochMs, actor: PersonRef.nullable(), role: RoleRef.nullable(), detail: AuditDetail.nullable() })),
  permissionChanges: z.array(z.object({ at: EpochMs, action: AuditActionCode, actor: PersonRef.nullable(), detail: AuditDetail.nullable() })),
  cases: z.array(z.object({ id: Id, code: z.string(), title: z.string(), status: CaseStatus, views: Count })),
  review: z.object({ verdict: z.enum(['ok', 'check']), memo: z.string().nullable(), by: PersonRef, at: EpochMs }).nullable(),
});
/** POST /api/v1/admin/audit/report/review — record the monthly check result (audit.review). */
export const AuditReviewRequest = z.object({ month: z.string().regex(/^\d{4}-\d{2}$/), verdict: z.enum(['ok', 'check']), memo: z.string().trim().max(200).optional() });

/** POST /api/v1/admin/audit/export — CSV of the filtered rows (export.rows all; real names need export.identity all + reason). */
export const AuditExportRequest = periodQuery({
  format: z.enum(['csv', 'json']).default('csv'),
  kinds: z.array(AuditChip).optional(),
  person: UserId.optional(),
  q: z.string().trim().max(60).optional(),
  identity: z.boolean().default(false),
  reasonCategory: ReasonCategory.optional(),
  reason: z.string().trim().max(200).optional(),
}).superRefine((b, ctx) => {
  if (b.identity && (!b.reasonCategory || !b.reason || b.reason.length < 10)) ctx.addIssue({ code: 'custom', message: 'identity export needs a reason', path: ['reason'] });
});

/** Person filter options (actors and targets that appear in the log), names per viewer permission. */
export const AuditPeopleResponse = z.object({ items: z.array(z.object({ person: PersonRef, role: RoleRef.nullable() })) });

export const adminAuditSchemas = {
  SeqParams,
  AuditListQuery,
  AuditListResponse,
  AuditEntryResponse,
  AuditKpisQuery,
  AuditKpisResponse,
  AuditVerifyResponse,
  AuditReportQuery,
  AuditReportResponse,
  AuditReviewRequest,
  AuditExportRequest,
  AuditPeopleResponse,
  AuditEntry,
} as const;
